import {DropdownMenu, IconButton} from '@wandb/components';
import clsx from 'clsx';
import React from 'react';

import {ArtifactVersionMetricConfigurePlaceholder} from './ArtifactVersionMetricChartPlaceholder';
import {ArtifactVersionMetricsPanelView} from './ArtifactVersionMetricsPanelView';
import type {ArtifactVersionMetricGridPanel} from './artifactVersionsGridTypes';
import {type ArtifactVersionMetricChartType, INITIAL_CONFIG} from './types';

/** Overflow menu (delete, etc.) shown in the top-right of a panel card. */
function ArtifactVersionMetricPanelMenu({onDelete}: {onDelete: () => void}) {
  const [isOpen, setIsOpen] = React.useState(false);
  return (
    <DropdownMenu.Trigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <IconButton
        aria-label="More panel actions"
        icon="overflow-horizontal"
        size="sm"
        variant="ghost"
      />
      <DropdownMenu.Content>
        <DropdownMenu.Item value={{action: 'delete'}} onAction={onDelete}>
          <DropdownMenu.ItemDecorationLeft iconProps={{icon: 'delete'}} />
          <DropdownMenu.ItemLabel>Delete panel</DropdownMenu.ItemLabel>
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Trigger>
  );
}

type ArtifactVersionMetricPanelCardProps = {
  artifactCollectionId?: string;
  canDeletePanel?: boolean;
  deletePanel: (panelId: string) => void;
  isEditing?: boolean;
  membershipIds: string[];
  onEditPanel?: (panel: ArtifactVersionMetricGridPanel) => void;
  onOpenFullscreen?: (panel: ArtifactVersionMetricGridPanel) => void;
  onSelectChartType?: (
    panel: ArtifactVersionMetricGridPanel,
    chartType: ArtifactVersionMetricChartType
  ) => void;
  panel: ArtifactVersionMetricGridPanel;
  readOnly?: boolean;
};

export const ArtifactVersionMetricPanelCard: React.FC<
  ArtifactVersionMetricPanelCardProps
> = ({
  artifactCollectionId,
  canDeletePanel = false,
  deletePanel,
  isEditing = false,
  membershipIds,
  onEditPanel,
  onOpenFullscreen,
  onSelectChartType,
  panel,
  readOnly,
}) => {
  const config = {
    ...INITIAL_CONFIG,
    ...panel.config,
    membershipIds,
  };
  const artifactVersionMetadata = {
    membershipIds,
    totalMemberships: membershipIds.length,
    loading: false,
  };
  const handleDelete = () => deletePanel(panel.__id__);
  const handleEditPanel = () => {
    onEditPanel?.(panel);
  };
  const handleOpenFullscreen = () => {
    onOpenFullscreen?.(panel);
  };
  const handleSelectChartType = (chartType: ArtifactVersionMetricChartType) => {
    onSelectChartType?.(panel, chartType);
  };

  if (config.chartType == null) {
    return (
      <div
        className="relative h-full min-h-[220px] min-w-0 w-full"
        data-test="artifact-version-metrics-panel-card"
        data-test-configured="false">
        {!readOnly && canDeletePanel && (
          <div className="absolute right-8 top-8 z-[1]">
            <ArtifactVersionMetricPanelMenu onDelete={handleDelete} />
          </div>
        )}
        <ArtifactVersionMetricConfigurePlaceholder
          onSelectChartType={readOnly ? undefined : handleSelectChartType}
        />
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'relative h-full min-h-[300px] min-w-0 w-full overflow-visible rounded-lg border border-solid border-wb-border bg-wb-surface',
        {'ring-2 ring-wb-accent': isEditing}
      )}
      data-test="artifact-version-metrics-panel-card"
      data-test-chart-type={config.chartType}
      data-test-configured="true">
      {(onOpenFullscreen != null || !readOnly) && (
        <div className="absolute right-8 top-8 z-[1] flex gap-4">
          {onOpenFullscreen != null && (
            <IconButton
              aria-label="View full screen"
              icon="full-screen-mode-expand"
              size="sm"
              tooltip={{children: 'View full screen'}}
              variant="ghost"
              onPress={handleOpenFullscreen}
            />
          )}
          {!readOnly && (
            <>
              <IconButton
                aria-label="Edit panel"
                icon="settings"
                size="sm"
                tooltip={{children: 'Edit panel'}}
                variant="ghost"
                onPress={handleEditPanel}
              />
              {canDeletePanel && (
                <ArtifactVersionMetricPanelMenu onDelete={handleDelete} />
              )}
            </>
          )}
        </div>
      )}
      <ArtifactVersionMetricsPanelView
        artifactCollectionId={artifactCollectionId}
        artifactVersionMetadata={artifactVersionMetadata}
        config={config}
        onEditPanel={readOnly ? undefined : handleEditPanel}
      />
    </div>
  );
};
